
import { useState, useEffect, useRef } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthUser } from '@/types/auth';
import { userDataService } from './userDataService';
import { withTimeout } from '@/utils/timeout';
import { authStateCache } from '@/utils/authStateCache';

export const useAuthState = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState<string>('Initializing your workspace...');
  const [usingCachedState, setUsingCachedState] = useState(false);
  
  // Track if we have a valid user to prevent fallback overwrites
  const [hasValidUser, setHasValidUser] = useState(false);
  
  // Track in-flight requests to prevent duplicates
  const fetchInProgressRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  const refreshUserData = async () => {
    if (!session?.user) return;
    
    // Prevent duplicate refreshes
    if (fetchInProgressRef.current) {
      console.log('[Auth] Refresh already in progress, skipping duplicate');
      return;
    }
    
    setLoadingMessage('Refreshing profile data...');
    fetchInProgressRef.current = true;
    
    try {
      const userData = await withTimeout(
        userDataService.fetchUserData(session.user),
        20000, // 20 second timeout for refresh
        'User data refresh timed out'
      );
      
      if (userData) {
        setUser(userData);
        setHasValidUser(true);
        setUsingCachedState(false);
        authStateCache.saveSnapshot(userData); // Save successful state
        console.log('[Auth] User data refreshed successfully');
      }
    } catch (error) {
      // Check if error is transient (network issue)
      const isTransientError = error instanceof Error && (
        error.message.includes('timeout') ||
        error.message.includes('network') ||
        error.message.includes('fetch')
      );

      if (isTransientError && user) {
        // Keep current state on transient errors
        console.warn('[Auth] Transient error, keeping current state:', error);
        setUsingCachedState(true);
      } else if (isTransientError) {
        // Try to use last known good snapshot
        const cachedSnapshot = authStateCache.getSnapshot();
        if (cachedSnapshot) {
          console.warn('[Auth] Using last known good snapshot');
          setUser(cachedSnapshot);
          setHasValidUser(true);
          setUsingCachedState(true);
        } else {
          console.error('[Auth] No cached state available');
        }
      } else {
        console.error('[Auth] Permanent error during refresh:', error);
      }
    } finally {
      setLoadingMessage('');
      fetchInProgressRef.current = false;
    }
  };

  useEffect(() => {
    let mounted = true;
    const abortController = new AbortController();
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted || abortController.signal.aborted) return;
        
        console.log('[Auth] Auth state change event:', event, 'Session:', !!session);
        
        setSession(session);
        
        if (session?.user) {
          // Check if this is the same user - prevent unnecessary re-fetches
          if (lastUserIdRef.current === session.user.id && hasValidUser) {
            console.log('[Auth] Same user detected, skipping re-fetch');
            return;
          }
          
          // Prevent duplicate fetches
          if (fetchInProgressRef.current) {
            console.log('[Auth] Fetch already in progress, skipping duplicate');
            return;
          }
          
          lastUserIdRef.current = session.user.id;
          setLoadingMessage('Loading your profile...');
          fetchInProgressRef.current = true;
          
          try {
            // Add timeout protection to initial user data fetch
            const userData = await withTimeout(
              userDataService.fetchUserData(session.user, hasValidUser),
              10000, // 10 second timeout for initial load
              'Initial user data fetch timed out'
            );
            
            if (mounted && !abortController.signal.aborted) {
              // Prevent role downgrade - never overwrite valid user with empty roles
              if (userData && (!userData.roles || userData.roles.length === 0) && hasValidUser && user?.roles?.length > 0) {
                console.warn('[Auth] Preventing role downgrade - userData has no roles but current user does');
                return; // Don't overwrite existing user
              }
              
              // Debug: Check if organization data is missing
              if (userData?.profile?.organization_id && !userData?.organization) {
                console.error('[Auth] Profile has organization_id but organization object is missing:', {
                  userId: userData.id,
                  organizationId: userData.profile.organization_id,
                  organizationName: userData.profile.organization_name
                });
              }
              setUser(userData);
              setHasValidUser(true);
              setUsingCachedState(false);
              authStateCache.saveSnapshot(userData); // Save successful state
              setLoading(false);
              setLoadingMessage('');
              console.log('[Auth] User data loaded successfully');
            }
          } catch (error) {
            console.warn('[Auth] User data fetch failed:', error);
            
            // Check if this is a transient error
            const isTransientError = error instanceof Error && (
              error.message.includes('timeout') ||
              error.message.includes('network') ||
              error.message.includes('fetch')
            );
            
            if (mounted && !abortController.signal.aborted) {
              if (isTransientError) {
                // Try to use last known good snapshot
                const cachedSnapshot = authStateCache.getSnapshot();
                if (cachedSnapshot) {
                  console.log('[Auth] Using last known good snapshot after transient error');
                  setUser(cachedSnapshot);
                  setHasValidUser(true);
                  setUsingCachedState(true);
                } else if (!hasValidUser) {
                  // Only create fallback if we don't have a valid user and no cache
                  console.log('[Auth] No cached snapshot, creating fallback user');
                  const fallbackUser = await userDataService.createFallbackUser(session.user);
                  setUser(fallbackUser);
                  setHasValidUser(false);
                }
              } else if (!hasValidUser) {
                // Permanent error and no valid user - fallback
                console.log('[Auth] Permanent error, creating fallback user');
                const fallbackUser = await userDataService.createFallbackUser(session.user);
                setUser(fallbackUser);
                setHasValidUser(false);
              } else {
                console.log('[Auth] Keeping existing user data despite error');
              }
              setLoading(false);
              setLoadingMessage('');
            }
          } finally {
            fetchInProgressRef.current = false;
          }
        } else {
          if (mounted && !abortController.signal.aborted) {
            console.log('[Auth] No session, clearing user state');
            setUser(null);
            setHasValidUser(false);
            lastUserIdRef.current = null;
            setLoading(false);
            setLoadingMessage('');
          }
        }
      }
    );

    // Check for existing session
    const initializeAuth = async () => {
      if (fetchInProgressRef.current) {
        console.log('[Auth] Initialization already in progress');
        return;
      }
      
      try {
        setLoadingMessage('Checking session...');
        fetchInProgressRef.current = true;
        
        const { data: { session }, error } = await withTimeout(
          supabase.auth.getSession(),
          8000, // 8 second timeout for session check
          'Session check timed out'
        );
        
        if (error) {
          console.error('[Auth] Session check error:', error);
          if (mounted && !abortController.signal.aborted) {
            setLoading(false);
            setLoadingMessage('');
          }
          return;
        }

        if (mounted && !abortController.signal.aborted) {
          setSession(session);
          
          if (session?.user) {
            lastUserIdRef.current = session.user.id;
            setLoadingMessage('Loading your profile...');
            
            try {
              const userData = await withTimeout(
                userDataService.fetchUserData(session.user, false),
                10000, // 10 second timeout for initial user data
                'Initial session user data fetch timed out'
              );
              
              if (mounted && !abortController.signal.aborted) {
                // Debug: Check if organization data is missing
                if (userData?.profile?.organization_id && !userData?.organization) {
                  console.error('[Auth] Profile has organization_id but organization object is missing:', {
                    userId: userData.id,
                    organizationId: userData.profile.organization_id,
                    organizationName: userData.profile.organization_name
                  });
                }
                setUser(userData);
                setHasValidUser(true);
                setUsingCachedState(false);
                authStateCache.saveSnapshot(userData); // Save successful state
                setLoadingMessage('');
              }
            } catch (error) {
              console.warn('[Auth] Initial user data fetch failed:', error);
              
              // Check if this is a transient error
              const isTransientError = error instanceof Error && (
                error.message.includes('timeout') ||
                error.message.includes('network') ||
                error.message.includes('fetch')
              );

              if (mounted && !abortController.signal.aborted) {
                if (isTransientError) {
                  // Try to use last known good snapshot
                  const cachedSnapshot = authStateCache.getSnapshot();
                  if (cachedSnapshot) {
                    console.log('[Auth] Using last known good snapshot for initial load');
                    setUser(cachedSnapshot);
                    setHasValidUser(true);
                    setUsingCachedState(true);
                  } else {
                    // Only create fallback if no cached snapshot exists
                    console.log('[Auth] No cached snapshot, creating fallback user');
                    const fallbackUser = await userDataService.createFallbackUser(session.user);
                    setUser(fallbackUser);
                    setHasValidUser(false);
                  }
                } else {
                  // Permanent error - don't use cache, report error clearly
                  console.error('[Auth] Permanent error during initial fetch, no fallback');
                }
                setLoadingMessage('');
              }
            }
          }
          
          setLoading(false);
        }
      } catch (error) {
        console.error('[Auth] Initialize auth error:', error);
        if (mounted && !abortController.signal.aborted) {
          setLoading(false);
          setLoadingMessage('');
        }
      } finally {
        fetchInProgressRef.current = false;
      }
    };

    initializeAuth();

    return () => {
      console.log('[Auth] Cleaning up auth state');
      mounted = false;
      abortController.abort();
      subscription.unsubscribe();
      fetchInProgressRef.current = false;
    };
  }, []);

  return {
    user,
    session,
    loading,
    loadingMessage,
    refreshUserData,
    usingCachedState
  };
};
